/**
 * Scroll handling for both the window and any nested scroll container
 * (containers opt in with a `data-scroll-container` attribute).
 *
 * `html` has `scroll-behavior: smooth` globally, so the behavior is always
 * passed explicitly - step changes should land at the top instantly, the way
 * a fresh page would.
 */

const SCROLL_CONTAINER_SELECTOR = "[data-scroll-container]";

export function scrollToTop({ smooth = false, immediate = false } = {}) {
  if (process.server) {
    return;
  }

  // `instant` is not accepted by older browsers - they throw on the unknown
  // enum value, so fall back to a plain scroll call there.
  const scrollElementToTop = (target, behavior) => {
    if (!target?.scrollTo) {
      return;
    }
    try {
      target.scrollTo({ top: 0, left: 0, behavior });
    } catch {
      target.scrollTo(0, 0);
    }
  };

  const scroll = () => {
    const behavior = smooth ? "smooth" : "instant";

    scrollElementToTop(window, behavior);
    scrollElementToTop(document.documentElement, behavior);
    scrollElementToTop(document.body, behavior);

    document
      .querySelectorAll(SCROLL_CONTAINER_SELECTOR)
      .forEach((el) => scrollElementToTop(el, behavior));
  };

  if (immediate) {
    scroll();
  } else {
    // Wait for the new step/page to render before scrolling.
    nextTick(scroll);
  }
}

/**
 * Scrolls to the top whenever `source` changes - use it in every component
 * that swaps steps without a route change.
 */
export function useScrollTopOnChange(source, options = {}) {
  watch(source, (value, oldValue) => {
    if (value === oldValue) {
      return;
    }
    scrollToTop(options);
  });
}
